import cloneDeep from 'lodash.clonedeep';
import React from 'react';
import './yaml-view.scss';

type Props = {
  data: { [key: string]: any } & { fromEdit?: boolean }
}

function adaptModifiedArray(key: string, before: any[], after: any[]): any[] {
  return before.map((item: any, index: number) => {

    const itemData = Object.keys(item).map((itemKey: string) => {
      const value = item[itemKey];
      if (typeof value !== 'object') {
        const afterValue = after[index]?.value;

        return {
          [itemKey]: {
            field: itemKey,
            before: value,
            after: afterValue,
            fromEdit: true,
          },
        };
      }

      if (Array.isArray(value)) {
        const optionsBefore = before[index]?.[itemKey];
        const afterBefore = after[index]?.[itemKey];

        const auditedOptions = optionsBefore.map((beforeOpt: any) => {
          const afterOptValue = afterBefore.find((afterOpt: any) => afterOpt.key === beforeOpt.key)?.value;
          const beforeOptValue = beforeOpt.value ?? afterOptValue;
          return {
            field: beforeOpt.key,
            before: beforeOptValue,
            after: afterOptValue ?? beforeOptValue,
            fromEdit: true,
          };
        })
          .reduce((t: any, a: any) => ({
            ...t,
            [a.field]: a,
          }), {});

        return {
          [itemKey]: {
            isList: true,
            data: auditedOptions,
          },
        };
      }

      return {
        [itemKey]: {
          itemKey,
        },
      };
    }).reduce((t: any, a: any) => ({ ...t, ...a }), {});

    return itemData;
  });
}

function YamlView({ data }: Props): JSX.Element {

  function renderModifiedField(key: string, before:string, after: string): JSX.Element {

    if (Array.isArray(before) && before?.length === 0 && after === '') {
      return (
        <div className="yaml-view-key-value">
          <span className="yaml-view-key">{key}: -</span>
        </div>
      );
    }

    // here we have arrays
    if (Array.isArray(before) && Array.isArray(after)) {
      const adaptedData = adaptModifiedArray(key, before, after);
      return (
        <div className="yaml-view-field">
          <div>
            {adaptedData.map((item, index) => {
              return (
                <div key={index}>
                  <span className="yaml-view-key-modified">{key}:</span>
                  <div className="yaml-view-field-label-icon">
                    <span className="ag-icon ag-icon-contracted" unselectable="on" />
                  </div>
                  <YamlView data={item} />
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    if (typeof before === 'object' && typeof after === 'object') {
      return (
        <div>
          <div className="yaml-view-key-value">
            {key}: {Object.keys(before).map((key) => {
            const beforeValue = before[key] as any;
            const afterValue = after[key] as any;

            if (beforeValue == null && afterValue == null) {
              return null;
            }
            return (
              <div className="yaml-view-field">
                <div className="yaml-view-field-label">
                  <div className="yaml-view-field-label-icon">
                    <span className="ag-icon ag-icon-contracted" unselectable="on" />
                  </div>
                  <div className="yaml-view-key-modified">
                    {key}:
                  </div>
                </div>
                <div className="yaml-view-children">
                  <YamlView data={before} />
                </div>
              </div>
            );
          })}
          </div>
        </div>
      );
    }

    if (before && before !== after) {
      return (
        <div className="yaml-view-key-value">
          <span className="yaml-view-key-modified">{key}: {data?.[key]?.after?.toString()}</span>{' '}<span className="yaml-view-key-value-modified">[Old Value - {key}: {data?.[key]?.before?.toString()}]</span>
        </div>
      );
    }

    if (before === '' && before !== after) {
      return (
        <div className="yaml-view-key-value">
          <span className="yaml-view-key-modified">{key}: {data?.[key]?.after?.toString()}</span>
        </div>
      );
    }

    return (
      <div className="yaml-view-field">
        <div className="yaml-view-key-value">
          {key}: {before?.toString()}
        </div>
      </div>
    );
  }

  function renderKey(key: string) {
    const value = data[key];

    if (value == null) {
      return null;
    }

    if (typeof value !== 'object') {
      return (
        <div className="yaml-view-key-value">
          {key}: {value.toString()}
        </div>
      );
    }

    if (data?.[key]?.isList) {
      const cloned = cloneDeep(data);

      return (
        <div className="yaml-view-field">
          <div className="yaml-view-field-label">
            <div className="yaml-view-field-label-icon">
              <span className="ag-icon ag-icon-contracted" unselectable="on" />
            </div>
            <div className="yaml-view-field-label-key">
              {key}:
            </div>
          </div>
          <div className="yaml-view-children">
            <YamlView data={cloned?.[key]?.data} />
          </div>
        </div>
      );

    }

    if (data?.[key]?.fromEdit) {
      const cloned = cloneDeep(data);
      return renderModifiedField(key, cloned?.[key]?.before, cloned?.[key]?.after);
    }

    return (
      <div className="yaml-view-field">
        <div className="yaml-view-field-label">
          <div className="yaml-view-field-label-icon">
            <span className="ag-icon ag-icon-contracted" unselectable="on" />
          </div>
          <div className="yaml-view-field-label-key">
            {key}:
          </div>
        </div>
        <div className="yaml-view-children">
          <YamlView data={value} />
        </div>
      </div>
    );
  }

  if (Object.keys(data).length === 0) {
    return <div className="yaml-view">-</div>;
  }

  return <div className="yaml-view">{Object.keys(data).map(renderKey)}</div>;
}
export default YamlView;
